<template>
  <controller>
    <page-header :title="isEditing ? 'Editar Unidade' : 'Nova Unidade'">
      <template v-slot:side>
        <span v-if="isEditing" :class="['status-badge', { inactive: !form.ativa }]">{{ form.ativa ? 'Ativa' : 'Inativa' }}</span>
        <router-link class="btn btn-secondary" :to="{ name: 'Unidades' }">Voltar</router-link>
      </template>
    </page-header>

    <main class="unit-form-page">

      <form-spinner v-if="loading" aria-label="Carregando formulário" />
      <section v-else-if="loadError" class="state-panel error" role="alert">
        <h2>Não foi possível abrir o cadastro</h2>
        <p>{{ loadError }}</p>
        <button type="button" class="btn btn-secondary" @click="load">Tentar novamente</button>
      </section>

      <form v-else class="form-shell" novalidate @submit.prevent="submit">
        <nav class="stepper" aria-label="Etapas do cadastro">
          <button
            v-for="(step, index) in steps"
            :key="step.title"
            type="button"
            :class="{ active: currentStep === index, completed: currentStep > index }"
            :aria-current="currentStep === index ? 'step' : null"
            :disabled="index > maxVisitedStep"
            @click="goToStep(index)"
          >
            <span>{{ currentStep > index ? '✓' : index + 1 }}</span>
            <span><strong>{{ step.title }}</strong><small>{{ step.description }}</small></span>
          </button>
        </nav>

        <section v-if="formError" class="form-alert" role="alert" tabindex="-1" ref="formAlert">
          <strong>{{ formErrorTitle }}</strong>
          <p>{{ formError }}</p>
        </section>

        <section class="form-content" :aria-labelledby="`step-${currentStep}-title`">
          <header>
            <span>Etapa {{ currentStep + 1 }} de {{ steps.length }}</span>
            <h2 :id="`step-${currentStep}-title`">{{ steps[currentStep].title }}</h2>
            <p>{{ steps[currentStep].help }}</p>
          </header>

          <div v-if="currentStep === 0" class="field-grid">
            <label><span>Nome jurídico <b>*</b></span><input v-model.trim="form.nome" maxlength="60" autocomplete="organization" :aria-invalid="Boolean(errors.nome)" :aria-describedby="errors.nome ? 'error-nome' : null"><small v-if="errors.nome" id="error-nome" class="field-error">{{ errors.nome }}</small></label>
            <label><span>Nome de exibição <b>*</b></span><input v-model.trim="form.descricao" maxlength="60" :aria-invalid="Boolean(errors.descricao)" :aria-describedby="errors.descricao ? 'error-descricao' : null"><small v-if="errors.descricao" id="error-descricao" class="field-error">{{ errors.descricao }}</small></label>
            <label><span>Sigla</span><input v-model.trim="form.sigla" maxlength="4" class="uppercase" placeholder="Ex.: SAU"></label>
            <label><span>Resumo</span><input v-model.trim="form.resumo" maxlength="15"></label>
            <label><span>CNPJ <b>*</b></span><input v-model.trim="form.cnpj" maxlength="20" inputmode="numeric" autocomplete="off" :aria-invalid="Boolean(errors.cnpj)" :aria-describedby="errors.cnpj ? 'error-cnpj' : null"><small v-if="errors.cnpj" id="error-cnpj" class="field-error">{{ errors.cnpj }}</small></label>
            <label><span>Inscrição municipal</span><input v-model.trim="form.inscricaomun" maxlength="11"></label>
            <label><span>Autorização</span><input v-model.trim="form.autorizacao" maxlength="20"></label>
            <label><span>Órgão autorizador</span><input v-model.trim="form.orgao" maxlength="20"></label>
            <label><span>Código INEP</span><input v-model.number="form.entidadeinep" type="number" min="0" inputmode="numeric"></label>
          </div>

          <div v-else-if="currentStep === 1" class="field-grid">
            <div class="city-field full-field">
              <label for="city-search"><span>Cidade</span></label>
              <div class="combobox-wrap">
                <input id="city-search" v-model="citySearch" type="search" autocomplete="off" role="combobox" aria-autocomplete="list" :aria-expanded="cityOpen" aria-controls="city-options" placeholder="Digite ao menos 2 letras" @input="scheduleCitySearch" @focus="openSelectedCity" @keydown.down.prevent="moveCity(1)" @keydown.up.prevent="moveCity(-1)" @keydown.enter.prevent="selectHighlightedCity" @keydown.esc="closeCities">
                <span v-if="cityLoading" class="field-loading" aria-live="polite">Buscando...</span>
                <ul v-if="cityOpen" id="city-options" role="listbox">
                  <li v-for="(city, index) in cities" :key="city.id" role="option" :aria-selected="highlightedCity === index" :class="{ highlighted: highlightedCity === index }" @mousedown.prevent="selectCity(city)">{{ city.label }}</li>
                  <li v-if="!cityLoading && !cities.length" class="empty-option">Nenhuma cidade encontrada.</li>
                </ul>
              </div>
              <small>Selecione uma opção da lista para vincular a cidade.</small>
            </div>
            <label class="wide-field"><span>Endereço</span><input v-model.trim="form.endereco" maxlength="50" autocomplete="street-address"></label>
            <label><span>Número</span><input v-model.trim="form.numero" maxlength="10" autocomplete="address-line2"></label>
            <label><span>Complemento</span><input v-model.trim="form.complemento" maxlength="35"></label>
            <label><span>Bairro</span><input v-model.trim="form.bairro" maxlength="40"></label>
            <label><span>CEP</span><input v-model.trim="form.cep" maxlength="10" inputmode="numeric" autocomplete="postal-code"></label>
            <label><span>Telefone</span><input v-model.trim="form.telefone" maxlength="35" type="tel" autocomplete="tel"></label>
            <label><span>E-mail geral</span><input v-model.trim="form.email" maxlength="80" type="email" autocomplete="email" :aria-invalid="Boolean(errors.email)"><small v-if="errors.email" class="field-error">{{ errors.email }}</small></label>
            <label><span>E-mail da coordenação</span><input v-model.trim="form.emailcoordenacao" maxlength="100" type="email" :aria-invalid="Boolean(errors.emailcoordenacao)"><small v-if="errors.emailcoordenacao" class="field-error">{{ errors.emailcoordenacao }}</small></label>
            <label><span>E-mail para comunicados</span><input v-model.trim="form.emailComunicado" maxlength="90" type="email" :aria-invalid="Boolean(errors.emailComunicado)"><small v-if="errors.emailComunicado" class="field-error">{{ errors.emailComunicado }}</small></label>
          </div>

          <div v-else-if="currentStep === 2" class="field-grid">
            <label><span>Cliente</span><select v-model="form.idcliente"><option :value="null">Não informado</option><option v-for="option in options.clientes" :key="option.id" :value="option.id">{{ option.label }}</option></select></label>
            <label><span>Empresa</span><select v-model="form.idEmpresa"><option :value="null">Não informada</option><option v-for="option in filteredCompanies" :key="option.id" :value="option.id">{{ option.label }}</option></select></label>
            <label><span>Curso padrão</span><select v-model="form.idCursopadrao"><option :value="null">Não informado</option><option v-for="option in filteredCourses" :key="option.id" :value="option.id">{{ option.label }}</option></select></label>
            <label><span>Sistema de ensino</span><select v-model="form.idSistemaEnsino"><option :value="null">Não informado</option><option v-for="option in options.sistemasEnsino" :key="option.id" :value="option.id">{{ option.label }}</option></select></label>
            <label><span>ID do sistema de conteúdo</span><input v-model.number="form.idSistemaEnsinoConteudo" type="number" min="0"></label>
            <label><span>ID do sistema central</span><input v-model.number="form.idSistemaEnsinoCentral" type="number" min="0"></label>
            <label><span>ID de integração</span><input v-model.number="form.idintegracao" type="number" min="0"></label>
            <label><span>Identificação organizacional</span><input v-model.trim="form.organizacional" maxlength="255"></label>
            <label><span>Tipo</span><input v-model.trim="form.tipo" maxlength="45"></label>
          </div>

          <div v-else-if="currentStep === 3" class="settings-layout">
            <section class="toggle-group" aria-labelledby="status-settings-title">
              <h3 id="status-settings-title">Status e comportamento</h3>
              <label class="toggle-field"><input v-model="form.ativa" type="checkbox"><span><strong>Unidade ativa</strong><small>Permite o uso da unidade nos fluxos operacionais.</small></span></label>
              <label class="toggle-field"><input v-model="form.educacional" type="checkbox"><span><strong>Unidade educacional</strong><small>Identifica locais com operação acadêmica.</small></span></label>
              <label class="toggle-field"><input v-model="form.requerCPF" type="checkbox"><span><strong>Exigir CPF</strong><small>Aplica a exigência aos fluxos que consultam essa configuração.</small></span></label>
              <label class="toggle-field"><input v-model="groupBills" type="checkbox"><span><strong>Agrupar boletos</strong><small>Salva “S” quando habilitado e “N” quando desabilitado.</small></span></label>
            </section>
            <section class="quota-group" aria-labelledby="quota-settings-title">
              <h3 id="quota-settings-title">Limites e matrícula</h3>
              <label><span>NCE gratuitos</span><input v-model.number="form.qtdNceGratuitos" type="number" min="0"></label>
              <label><span>Optativas gratuitas</span><input v-model.number="form.qtdOptGratuitos" type="number" min="0"></label>
              <label><span>CDF gratuitos</span><input v-model.number="form.qtdCdfGratuitos" type="number" min="0"></label>
              <label><span>Vídeo de matrícula</span><input v-model.trim="form.videoMatricula" type="url" maxlength="255" placeholder="https://..." :aria-invalid="Boolean(errors.videoMatricula)"><small v-if="errors.videoMatricula" class="field-error">{{ errors.videoMatricula }}</small></label>
              </section>
          </div>
        </section>

        <footer class="form-actions">
          <router-link class="btn btn-secondary" :to="{ name: 'Unidades' }">Cancelar</router-link>
          <div>
            <button v-if="currentStep > 0" type="button" class="btn btn-secondary" :disabled="saving" @click="previousStep">Voltar</button>
            <button v-if="currentStep < steps.length - 1" type="button" class="btn btn-primary" @click="nextStep">Continuar</button>
            <button v-else type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : (isEditing ? 'Salvar alterações' : 'Criar unidade') }}</button>
          </div>
        </footer>
      </form>
    </main>
  </controller>
</template>

<script>
import UnidadesService from '@/services/unidades.service'

const emptyForm = () => ({
  idCidade: null, nome: '', telefone: '', endereco: '', numero: '', complemento: '', bairro: '', cep: '',
  descricao: '', cnpj: '', autorizacao: '', orgao: '', resumo: '', idcliente: null, idCursopadrao: null,
  sigla: '', inscricaomun: '', email: '', idintegracao: null, emailcoordenacao: '', entidadeinep: null,
  idEmpresa: null, educacional: true, organizacional: '', tipo: '', ativa: true, emailComunicado: '',
  requerCPF: false, idSistemaEnsino: null, agruparBoletos: 'N', videoMatricula: '', qtdNceGratuitos: null,
  qtdOptGratuitos: null, qtdCdfGratuitos: null, idSistemaEnsinoConteudo: null, idSistemaEnsinoCentral: null
})

export default {
  name: 'UnidadeForm',
  props: { idUnidade: { type: [String, Number], default: null } },
  data () {
    return {
      steps: [
        { title: 'Identificação', description: 'Dados legais', help: 'Informe os dados que identificam a unidade nos sistemas e documentos.' },
        { title: 'Endereço e contato', description: 'Localização', help: 'Cadastre a localização e os canais usados para contato e comunicação.' },
        { title: 'Vínculos', description: 'Relações', help: 'Relacione cliente, empresa, curso e sistemas acadêmicos.' },
        { title: 'Configurações', description: 'Comportamento', help: 'Revise os status e parâmetros operacionais antes de salvar.' }
      ],
      currentStep: 0,
      maxVisitedStep: 0,
      form: emptyForm(),
      options: { clientes: [], cursos: [], empresas: [], sistemasEnsino: [] },
      cities: [], citySearch: '', cityOpen: false, cityLoading: false, highlightedCity: -1, cityTimer: null,
      loading: true, saving: false, loadError: '', formError: '', formErrorTitle: 'Revise o preenchimento', errors: {}
    }
  },
  computed: {
    isEditing () { return Boolean(this.idUnidade) },
    filteredCompanies () { return this.filterByClient(this.options.empresas) },
    filteredCourses () { return this.filterByClient(this.options.cursos) },
    groupBills: {
      get () { return this.form.agruparBoletos === 'S' },
      set (value) { this.form.agruparBoletos = value ? 'S' : 'N' }
    }
  },
  watch: {
    'form.idcliente' () {
      if (!this.filteredCompanies.some(option => option.id === this.form.idEmpresa)) this.form.idEmpresa = null
      if (!this.filteredCourses.some(option => option.id === this.form.idCursopadrao)) this.form.idCursopadrao = null
    }
  },
  mounted () { this.load() },
  beforeUnmount () { clearTimeout(this.cityTimer) },
  methods: {
    async load () {
      this.loading = true; this.loadError = ''
      try {
        const requests = [UnidadesService.getOptions()]
        if (this.isEditing) requests.push(UnidadesService.get(this.idUnidade))
        const [optionsResponse, unitResponse] = await Promise.all(requests)
        this.options = optionsResponse.data
        if (unitResponse) {
          this.form = { ...emptyForm(), ...unitResponse.data.data }
          ;['idCidade', 'idcliente', 'idCursopadrao', 'idEmpresa', 'idSistemaEnsino'].forEach(field => {
            if (Number(this.form[field]) === 0) this.form[field] = null
          })
          this.form.ativa = Boolean(this.form.ativa)
          this.form.educacional = Boolean(this.form.educacional)
          this.form.requerCPF = Boolean(this.form.requerCPF)
          this.citySearch = [this.form.cidade, this.form.uf].filter(Boolean).join(' / ')
        }
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Verifique sua conexão e tente novamente.'
      } finally { this.loading = false }
    },
    filterByClient (items) {
      if (!this.form.idcliente) return items
      return items.filter(option => !option.idcliente || Number(option.idcliente) === Number(this.form.idcliente))
    },
    validateStep () {
      this.errors = {}; this.formError = ''; this.formErrorTitle = 'Revise o preenchimento'
      if (this.currentStep === 0) {
        if (!this.form.nome || this.form.nome.length < 3) this.errors.nome = 'Informe ao menos 3 caracteres.'
        if (!this.form.descricao) this.errors.descricao = 'Informe o nome de exibição.'
        if (!this.form.cnpj) this.errors.cnpj = 'Informe o CNPJ.'
      }
      if (this.currentStep === 1) {
        ['email', 'emailcoordenacao', 'emailComunicado'].forEach(field => {
          if (this.form[field] && !/^\S+@\S+\.\S+$/.test(this.form[field])) this.errors[field] = 'Informe um e-mail válido.'
        })
      }
      if (this.currentStep === 3 && this.form.videoMatricula && !/^https?:\/\//i.test(this.form.videoMatricula)) this.errors.videoMatricula = 'Informe uma URL iniciada por http:// ou https://.'
      if (Object.keys(this.errors).length) {
        this.formError = 'Há campos inválidos nesta etapa.'
        this.focusAlert()
        return false
      }
      return true
    },
    nextStep () {
      if (!this.validateStep()) return
      this.currentStep += 1
      this.maxVisitedStep = Math.max(this.maxVisitedStep, this.currentStep)
      this.scrollTop()
    },
    previousStep () { this.currentStep -= 1; this.formError = ''; this.formErrorTitle = 'Revise o preenchimento'; this.errors = {}; this.scrollTop() },
    goToStep (index) { if (index <= this.maxVisitedStep) { this.currentStep = index; this.formError = ''; this.formErrorTitle = 'Revise o preenchimento'; this.errors = {}; this.scrollTop() } },
    scrollTop () { window.pagescroll.emit('scrollTop') },
    focusAlert () { this.$nextTick(() => this.$refs.formAlert?.focus()) },
    scheduleCitySearch () {
      this.form.idCidade = null
      clearTimeout(this.cityTimer)
      if (this.citySearch.trim().length < 2) { this.cities = []; this.cityOpen = false; return }
      this.cityTimer = setTimeout(this.searchCities, 300)
    },
    async searchCities () {
      this.cityLoading = true; this.cityOpen = true; this.highlightedCity = -1
      try { const response = await UnidadesService.searchCities({ busca: this.citySearch.trim() }); this.cities = response.data.data || [] }
      catch (error) { this.cities = []; this.formError = error.response?.data?.message || 'Não foi possível buscar cidades.' }
      finally { this.cityLoading = false }
    },
    openSelectedCity () { if (this.cities.length) this.cityOpen = true },
    moveCity (direction) { if (!this.cityOpen || !this.cities.length) return; this.highlightedCity = (this.highlightedCity + direction + this.cities.length) % this.cities.length },
    selectHighlightedCity () { if (this.highlightedCity >= 0) this.selectCity(this.cities[this.highlightedCity]) },
    selectCity (city) { this.form.idCidade = city.id; this.citySearch = city.label; this.cityOpen = false; this.highlightedCity = -1 },
    closeCities () { this.cityOpen = false; this.highlightedCity = -1 },
    payload () {
      const payload = { ...this.form }
      ;['cidade', 'uf', 'cliente', 'cursoPadrao', 'empresa', 'sistemaEnsino', 'idUnidade', 'integraAgendaEdu', 'agendaEduId'].forEach(field => delete payload[field])
      Object.keys(payload).forEach(key => { if (payload[key] === '') payload[key] = null })
      return payload
    },
    async submit () {
      if (!this.validateStep() || this.saving) return
      this.saving = true; this.formError = ''; this.formErrorTitle = 'Revise o preenchimento'
      try {
        const response = this.isEditing ? await UnidadesService.update(this.idUnidade, this.payload()) : await UnidadesService.create(this.payload())
        window.toaster.emit('open', { style: 'success', message: response.data.message, floater: true })
        this.$router.push({ name: 'Unidades' })
      } catch (error) {
        const data = error.response?.data
        if (data && typeof data === 'object' && !data.message) {
          this.errors = Object.fromEntries(Object.entries(data).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value]))
          const fieldSteps = {
            nome: 0, descricao: 0, cnpj: 0, sigla: 0, entidadeinep: 0,
            idCidade: 1, telefone: 1, endereco: 1, cep: 1, email: 1,
            emailcoordenacao: 1, emailComunicado: 1,
            idcliente: 2, idEmpresa: 2, idCursopadrao: 2, idSistemaEnsino: 2,
            videoMatricula: 3, ativa: 3, educacional: 3, requerCPF: 3
          }
          const errorSteps = Object.keys(this.errors).map(field => fieldSteps[field]).filter(step => step !== undefined)
          if (errorSteps.length) {
            this.currentStep = Math.min(...errorSteps)
            this.maxVisitedStep = Math.max(this.maxVisitedStep, this.currentStep)
          }
          this.formError = 'A API recusou alguns campos. Revise os valores informados.'
        } else this.formError = data?.message || 'Não foi possível salvar a unidade.'
        this.focusAlert()
      } finally { this.saving = false }
    }
  }
}
</script>

<style lang="scss" scoped>
.unit-form-page { max-width: 1180px; margin: 0 auto; padding: $mg_mid; color: $color-dev; }
.form-shell, .state-panel { background: $color-secondary; border: $border-component; border-radius: $border-radius; overflow: hidden; }
.stepper { display: grid; grid-template-columns: repeat(4, 1fr); border-bottom: $border-component; }
.stepper button { display: flex; align-items: center; gap: $mg_mini; min-height: 76px; padding: $mg_small; border: 0; border-right: $border-component; background: $color-secondary; text-align: left; color: $color-text; }
.stepper button:last-child { border-right: 0; }
.stepper button > span:first-child { display: grid; place-items: center; flex: 0 0 32px; height: 32px; border: $border-component; border-radius: 50%; font-weight: 700; }
.stepper button > span:last-child { display: flex; flex-direction: column; }
.stepper strong { color: $color-dev; }
.stepper small { margin-top: $hmg_mini; }
.stepper .active { box-shadow: inset 0 -3px 0 $color-primary; }
.stepper .active > span:first-child, .stepper .completed > span:first-child { color: $color-secondary; background: $color-primary; border-color: $color-primary !important; }
.stepper button:disabled { opacity: .55; cursor: not-allowed; }
.form-alert { margin: $hmg_mid $hmg_mid 0; padding: $mg_small; border: 1px solid $color-error; border-radius: $border-radius-mid; background: $color-bg; }
.form-alert p { margin: $hmg_mini 0 0; }
.form-content { padding: $hmg_mid; min-height: 420px; }
.form-content > header { max-width: 720px; margin-bottom: $mg_mid; }
.form-content > header span { color: $color-primary; font-size: $font-size-small; font-weight: 700; }
.form-content h2 { margin: $hmg_mini 0; font-size: 22px; }
.form-content > header p { margin: 0; color: $color-text; }
.field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $hmg_mid; }
label, .city-field { display: flex; flex-direction: column; gap: $hmg_mini; font-weight: 600; }
label > span b { color: $color-primary; }
input, select { width: 100%; min-height: 44px; padding: 0 $mg_small; color: $color-dev; background: $color-secondary; border: $border-component; border-radius: $border-radius-mid; }
input[aria-invalid="true"] { border-color: $color-error !important; }
input:focus-visible, select:focus-visible, button:focus-visible, a:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.uppercase { text-transform: uppercase; }
.full-field { grid-column: 1 / -1; }
.wide-field { grid-column: span 1; }
.field-error { color: $color-error; font-weight: 600; }
.city-field > small { color: $color-text; font-weight: 400; }
.combobox-wrap { position: relative; }
.combobox-wrap ul { position: absolute; z-index: 20; top: calc(100% + #{$hmg_mini}); left: 0; right: 0; max-height: 240px; overflow-y: auto; margin: 0; padding: $hmg_mini; list-style: none; background: $color-secondary; border: $border-component; border-radius: $border-radius-mid; }
.combobox-wrap li { padding: $mg_mini; border-radius: $border-radius-small; cursor: pointer; }
.combobox-wrap li:hover, .combobox-wrap li.highlighted { background: $color-bg; color: $color-primary; }
.combobox-wrap .empty-option { color: $color-text; cursor: default; }
.field-loading { position: absolute; right: $mg_small; top: 50%; transform: translateY(-50%); color: $color-text; font-size: $font-size-small; }
.settings-layout { display: grid; grid-template-columns: 1.2fr 1fr; gap: $mg_mid; }
.settings-layout section { border: $border-component; border-radius: $border-radius-mid; padding: $hmg_mid; }
.settings-layout h3 { margin: 0 0 $hmg_mid; font-size: 17px; }
.toggle-group { display: flex; flex-direction: column; gap: $mg_small; }
.toggle-field { flex-direction: row; align-items: flex-start; gap: $mg_mini; font-weight: 400; }
.toggle-field input { flex: 0 0 20px; width: 20px; min-height: 20px; margin-top: $hmg_mini; accent-color: $color-primary; }
.toggle-field span { display: flex; flex-direction: column; }
.toggle-field small { color: $color-text; margin-top: $hmg_mini; }
.quota-group { display: grid; grid-template-columns: 1fr 1fr; gap: $mg_small; }
.quota-group h3, .quota-group label:last-child { grid-column: 1 / -1; }
.form-actions { display: flex; justify-content: space-between; align-items: center; gap: $mg_small; padding: $hmg_small $hmg_mid; border-top: $border-component; background: $color-bg; }
.form-actions > div { display: flex; gap: $mg_mini; }
.status-badge { display: inline-flex; align-items: center; min-height: 30px; padding: 0 $mg_mini; border-radius: $border-radius-xlarge; background: $color-active-light; font-weight: 700; }
.status-badge.inactive { background: $color-bg; border: $border-component; }
.state-panel { text-align: center; padding: $mg; }
.state-panel.error { border-color: $color-error !important; }
@media (max-width: 1024px) {
  .unit-form-page { padding: $hmg_mid; }
  .stepper { grid-template-columns: repeat(2, 1fr); }
  .stepper button { border-bottom: $border-component; }
  .stepper button:nth-child(even) { border-right: 0; }
  .stepper button:last-child { border-bottom: 0; }
  .settings-layout { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .unit-form-page { padding: $mg_small; }
  .stepper { display: flex; overflow-x: auto; }
  .stepper button { min-width: 190px; border-bottom: 0 !important; }
  .field-grid, .quota-group { grid-template-columns: 1fr; }
  .full-field, .wide-field, .quota-group h3, .quota-group label:last-child { grid-column: auto; }
  .form-content { padding: $mg_small; }
  .form-alert { margin: $mg_small $mg_small 0; }
  .form-actions { align-items: stretch; flex-direction: column-reverse; padding: $mg_small; }
  .form-actions > div { flex-direction: column; width: 100%; }
  .form-actions .btn { width: 100%; min-height: 44px; }
}
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { scroll-behavior: auto !important; transition: none !important; } }
</style>
